import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import WarningLoading from './WarningLoading';
import RetryNotice from './RetryNotice';
import SetBy from './SetBy';
import apiRequest from '../../api/request';
import { WD, FONT_SERIF } from './warningTheme';

// "Who can see CRO Workflow" - the second tab of Manage access (Finance only). The same screen as "Who can see Performance Metrics" with its
// own switches: one per department, and an Exceptions box for people who must still see it. The CRO Workflow section of the landing page, its
// 13 stage cards and the CRO Pending Tasks page are shown to everyone unless their department is switched off here; Performance Metrics and
// Pending Approvals are not affected; Finance can never be switched off. OGD starts switched off (its staff are never a student's CRO, so the
// section is all zeros for them) - the "Default" label shows that nobody did it by hand. Server side: warningDashboard/croWorkflowAccess.js
// (the rule) and cro_workflow_access.controller.js (this screen's endpoints).
const BASE = 'warning-dashboard/access/cro-workflow';
const DEFAULT_LABEL = 'Default'; // what the server sends for a department nobody switched off by hand (the sheet starts OGD off)
const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;
const failure = (res, fallback) => res?.message || fallback;

export const CroWorkflowAccessPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // the screen could not load
  const [actionError, setActionError] = useState(null); // a save failed
  const [filter, setFilter] = useState('');
  const [confirm, setConfirm] = useState(null); // the department about to be switched off
  const [busy, setBusy] = useState(null); // what is being saved right now

  // exceptions: search box + the person picked
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);

  const load = useCallback((quiet = false) => {
    if (!quiet) setLoading(true);
    // apiRequest resolves even on a failed call (src/api/request.js), so the status check is required
    return apiRequest({ url: BASE, method: 'get' })
      .then((res) => {
        if (res?.status !== 'success') { setError(failure(res, 'Could not load who can see CRO Workflow')); return; }
        setData(res.data);
        setError(null);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Could not load who can see CRO Workflow'))
      .finally(() => { if (!quiet) setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setOptions([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      apiRequest({ url: `${BASE}/people?search=${encodeURIComponent(text)}`, method: 'get' })
        .then((res) => { if (!cancelled && res?.status === 'success') setOptions(res.data?.people || []); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  // one save at a time; on success the list is re-read (quietly, no spinner) so it always shows what the server holds
  const save = (key, request) => {
    setBusy(key);
    setActionError(null);
    return request
      .then((res) => {
        if (res?.status !== 'success') { setActionError(failure(res, 'Could not save')); return false; }
        return load(true).then(() => true);
      })
      .catch((err) => { setActionError(err?.response?.data?.message || 'Could not save'); return false; })
      .finally(() => setBusy(null));
  };
  const setCanSee = (dept, canSee) => save(`d${dept.id}`, apiRequest({ url: `${BASE}/departments/${dept.id}`, method: 'put', data: { can_see: canSee } }));
  const addException = () => save('add', apiRequest({ url: `${BASE}/exceptions`, method: 'post', data: { user_id: picked.userId } }))
    .then((ok) => { if (ok) { setPicked(null); setQuery(''); setOptions([]); } });
  const removeException = (e) => save(`e${e.userId}`, apiRequest({ url: `${BASE}/exceptions/${e.userId}`, method: 'delete' }));

  const departments = (data?.departments || []).filter((d) => d.name.toLowerCase().includes(filter.trim().toLowerCase()));
  const locked = !!data?.tablesMissing;

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Everyone who can open the Warning Dashboard sees CRO Workflow - its section on the landing page and the CRO Pending Tasks page -
        unless their department is switched off below. Performance Metrics and Pending Approvals are not affected. Finance always sees it.
      </Typography>

      {loading && <WarningLoading />}
      {!loading && error && <RetryNotice message={error} onRetry={() => load()} />}
      {actionError && <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>{actionError}</Alert>}
      {!loading && !error && locked && <Alert severity="warning" sx={{ mb: 2 }}>{data.tablesMissingMessage}</Alert>}

      {!loading && !error && data && (
        <>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
            <Box sx={{ bgcolor: WD.purpleTint, color: WD.purple, borderRadius: '999px', px: 1.75, py: 0.5, fontSize: 13, fontWeight: 700 }}>
              {data.summary.canSee} of {data.summary.departments} departments can see CRO Workflow
            </Box>
            <TextField size="small" placeholder="Search departments" value={filter} onChange={(e) => setFilter(e.target.value)} inputProps={{ 'aria-label': 'Search departments' }} sx={{ width: 260 }} />
          </Box>

          <TableContainer sx={{ maxWidth: 900, mb: 5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell align="right"><strong>People</strong></TableCell>
                  <TableCell><strong>Can see CRO Workflow</strong></TableCell>
                  <TableCell><strong>Changed</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departments.map((d) => (
                  <TableRow key={d.id} hover sx={d.canSee ? undefined : { backgroundColor: '#FDF1F0' }}>
                    <TableCell>{d.name}</TableCell>
                    <TableCell align="right">{d.people}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Switch
                          size="small"
                          checked={d.canSee}
                          disabled={d.isFinance || locked || busy === `d${d.id}`}
                          onChange={(e) => (e.target.checked ? setCanSee(d, true) : setConfirm(d))}
                          inputProps={{ 'aria-label': `CRO Workflow for ${d.name}` }}
                        />
                        <Typography variant="body2" fontWeight={d.canSee ? 400 : 700} color={d.canSee ? 'text.primary' : 'error.main'}>
                          {d.isFinance ? 'Always on' : d.canSee ? 'On' : 'Off'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {d.switchedOffBy === DEFAULT_LABEL
                        ? <Typography variant="caption" color="text.secondary" component="div">Off by default</Typography>
                        : <SetBy name={d.switchedOffBy} at={d.switchedOffAt} label="Switched off by" />}
                    </TableCell>
                  </TableRow>
                ))}
                {departments.length === 0 && (
                  <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No department matches "{filter}".</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography component="h2" sx={{ fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 600, color: WD.ink }}>Exceptions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
            People who still see CRO Workflow even though their department is switched off.
          </Typography>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              sx={{ width: 340 }}
              options={options}
              value={picked}
              disabled={locked}
              onChange={(e, v) => setPicked(v)}
              onInputChange={(e, v) => setQuery(v)}
              inputValue={query}
              filterOptions={(x) => x}
              getOptionLabel={(p) => p.name}
              getOptionDisabled={(p) => p.alreadyException}
              isOptionEqualToValue={(a, b) => a.userId === b.userId}
              noOptionsText={query.trim().length < 2 ? 'Type at least 2 letters' : 'No one found'}
              renderOption={(props, p) => (
                <li {...props} key={p.userId}>
                  <Box>
                    <Typography variant="body2">{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.departments.join(', ') || 'No department'}
                      {p.alreadyException ? ' · already an exception' : p.departmentOff ? ' · department is off' : ' · department is on'}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Add a person" />}
            />
            <Button variant="contained" disabled={!picked || locked || busy === 'add'} onClick={addException}>Add</Button>
          </Box>

          {data.exceptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No exceptions. Everyone in a switched-off department is hidden.</Typography>
          ) : (
            <TableContainer sx={{ maxWidth: 900 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                    <TableCell><strong>Person</strong></TableCell>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Added</strong></TableCell>
                    <TableCell align="right"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.exceptions.map((e) => (
                    <TableRow key={e.userId} hover>
                      <TableCell>{e.name}{!e.active && ' (inactive)'}</TableCell>
                      <TableCell>{e.departments.join(', ') || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={e.needed ? 'success.main' : 'text.secondary'}>
                          {e.needed ? 'Needed: their department is off' : 'Not needed yet: their department is on'}
                        </Typography>
                      </TableCell>
                      <TableCell><SetBy name={e.addedBy} at={e.addedAt} label="Added by" /></TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" disabled={locked || busy === `e${e.userId}`} onClick={() => removeException(e)}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Hide CRO Workflow from {confirm?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm && `${people(confirm.people)} in ${confirm.name} will no longer see the CRO Workflow section or the CRO Pending Tasks page. `}
            Performance Metrics and Pending Approvals are not affected, and you can switch it back on at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => { const d = confirm; setConfirm(null); setCanSee(d, false); }}>
            {confirm ? `Hide from ${people(confirm.people)}` : 'Hide'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CroWorkflowAccessPanel;
